import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Alert,
  Chip,
} from '@mui/material';
import { Delete, Save, Add } from '@mui/icons-material';
import { WorkerSchedule } from '../types';
import { workerScheduleService } from '../services/workerScheduleService';

interface WorkerScheduleManagerProps {
  workerId: number;
  workerName: string;
}

const DAYS_OF_WEEK = [
  { value: 'Monday', label: 'Lunes' },
  { value: 'Tuesday', label: 'Martes' },
  { value: 'Wednesday', label: 'Miércoles' },
  { value: 'Thursday', label: 'Jueves' },
  { value: 'Friday', label: 'Viernes' },
  { value: 'Saturday', label: 'Sábado' },
  { value: 'Sunday', label: 'Domingo' },
];

export const WorkerScheduleManager: React.FC<WorkerScheduleManagerProps> = ({
  workerId,
  workerName,
}) => {
  const [schedules, setSchedules] = useState<WorkerSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Estado para el formulario de nuevo horario
  const [newSchedule, setNewSchedule] = useState({
    day_of_week: '',
    entry_time: '',
    exit_time: '',
  });

  useEffect(() => {
    loadSchedules();
  }, [workerId]);

  const loadSchedules = async () => {
    try {
      setLoading(true);
      const data = await workerScheduleService.getWorkerSchedules(workerId);
      setSchedules(data);
      setError(null);
    } catch (err) {
      setError('Error al cargar los horarios');
      console.error('Error loading schedules:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSchedule = async () => {
    try {
      if (!newSchedule.day_of_week || !newSchedule.entry_time || !newSchedule.exit_time) {
        setError('Por favor completa todos los campos');
        return;
      }

      // Validar que las horas no sean iguales (pero permitir horarios nocturnos)
      if (newSchedule.entry_time === newSchedule.exit_time) {
        setError('La hora de entrada no puede ser igual a la hora de salida');
        return;
      }

      await workerScheduleService.saveWorkerSchedule(workerId, {
        day_of_week: newSchedule.day_of_week,
        entry_time: newSchedule.entry_time + ':00',
        exit_time: newSchedule.exit_time + ':00',
      });

      setSuccess(`Horario para ${DAYS_OF_WEEK.find(d => d.value === newSchedule.day_of_week)?.label} guardado exitosamente`);
      setNewSchedule({ day_of_week: '', entry_time: '', exit_time: '' });
      await loadSchedules();
    } catch (error) {
      console.error('Error saving schedule:', error);
      setError('Error al guardar el horario');
    }
  };

  const handleDeleteSchedule = async (dayOfWeek: string) => {
    try {
      await workerScheduleService.deleteWorkerSchedule(workerId, dayOfWeek);
      setSuccess('Horario eliminado exitosamente');
      await loadSchedules();
    } catch (error) {
      console.error('Error deleting schedule:', error);
      setError('Error al eliminar el horario');
    }
  };

  const getDayLabel = (dayValue: string) => {
    return DAYS_OF_WEEK.find(d => d.value === dayValue)?.label || dayValue;
  };

  const formatTime = (time: string) => {
    return time.substring(0, 5); // Convertir HH:mm:ss a HH:mm
  };

  if (loading) {
    return <Typography>Cargando horarios...</Typography>;
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Horarios de {workerName}
      </Typography>

      {/* Alertas */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Formulario para agregar nuevo horario */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Agregar Nuevo Horario
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Día de la semana</InputLabel>
            <Select
              value={newSchedule.day_of_week}
              label="Día de la semana"
              onChange={(e) => setNewSchedule({ ...newSchedule, day_of_week: e.target.value })}
            >
              {DAYS_OF_WEEK.map((day) => (
                <MenuItem key={day.value} value={day.value}>
                  {day.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Hora de entrada"
            type="time"
            value={newSchedule.entry_time}
            onChange={(e) => setNewSchedule({ ...newSchedule, entry_time: e.target.value })}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />

          <TextField
            label="Hora de salida"
            type="time"
            value={newSchedule.exit_time}
            onChange={(e) => setNewSchedule({ ...newSchedule, exit_time: e.target.value })}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />

          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSaveSchedule}
            sx={{ minWidth: 120 }}
          >
            Guardar
          </Button>
        </Box>
      </Paper>

      {/* Tabla de horarios */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Día</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Hora de Entrada</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Hora de Salida</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {schedules.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography color="textSecondary">
                      No hay horarios configurados
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                schedules.map((schedule) => (
                  <TableRow key={schedule.id} hover>
                    <TableCell>
                      <Chip 
                        label={getDayLabel(schedule.day_of_week)} 
                        color="primary" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {formatTime(schedule.entry_time)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {formatTime(schedule.exit_time)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteSchedule(schedule.day_of_week)}
                        sx={{ color: '#f44336' }}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};
